import { MeetingRoom } from "@/lib/api/client";

export interface RoomFilters {
  /** Free-text match against room name + number (description). */
  query: string;
  /** Minimum seat count, or null for no seat filter. */
  minSeats: number | null;
  /** Required equipment keys; a room must have ALL selected. */
  equipment: string[];
}

/**
 * True if a single room satisfies the given filters.
 *
 * Note: a room with unknown seat count (`seats == null`) does NOT match when a
 * `minSeats` filter is active — a seats filter means "only rooms I can confirm
 * fit".
 */
export function roomMatchesFilters(room: MeetingRoom, filters: RoomFilters): boolean {
  const trimmedQuery = filters.query.trim().toLowerCase();
  const matchesText =
    !trimmedQuery ||
    [room.name, room.description].filter(Boolean).join(" ").toLowerCase().includes(trimmedQuery);
  const matchesSeats = filters.minSeats == null || (room.seats != null && room.seats >= filters.minSeats);
  const matchesEquipment = filters.equipment.every((eq) =>
    eq === "camera" ? room.has_camera : eq === "display" ? room.has_tv : eq === "speaker" ? room.has_speaker : true,
  );
  return matchesText && matchesSeats && matchesEquipment;
}

/** Filter a room list; returns the input unchanged when no filters are set. */
export function filterRooms(rooms: MeetingRoom[], filters: RoomFilters): MeetingRoom[] {
  const hasFilters =
    filters.query.trim() !== "" || filters.minSeats != null || filters.equipment.length > 0;
  return hasFilters ? rooms.filter((room) => roomMatchesFilters(room, filters)) : rooms;
}
